import websocketService from '../websocket';

// Track loading states for orders
const loadingStates = {
  placeOrder: false,
  cancelOrder: false
};

// Event emitter for loading state changes
const loadingStateListeners = new Set();

// Function to notify listeners of loading state changes
const notifyLoadingStateChange = (operation, isLoading) => {
  console.log(`Loading state changed for ${operation}: ${isLoading}`);
  loadingStateListeners.forEach(listener => listener(operation, isLoading));
};

/**
 * Subscribe to loading state changes
 * @param {Function} callback - Function to call when loading state changes
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToLoadingState = (callback) => {
  loadingStateListeners.add(callback);
  return () => loadingStateListeners.delete(callback);
};

/**
 * Place a new order through the WebSocket connection
 * @param {Object} orderParams - Order parameters
 * @param {number} orderParams.price - Order price
 * @param {number} orderParams.quantity - Order quantity
 * @param {number} orderParams.leverage - Leverage amount
 * @param {string} orderParams.orderType - Order type (market/limit)
 * @param {string} orderParams.side - Order side (buy/sell)
 * @param {string} orderParams.symbol - Trading symbol
 * @param {number} [orderParams.takeProfit] - Take profit price
 * @param {number} [orderParams.stopLoss] - Stop loss price
 * @param {string} [orderParams.mode] - Order mode (open/close)
 * @returns {Promise<Object>} - Resolves with the order response or rejects with an error
 */
export const placeOrder = (orderParams) => {
  // Check if already loading
  if (loadingStates.placeOrder) {
    console.log('Order placement already in progress, rejecting duplicate order');
    return Promise.reject(new Error('Order placement already in progress'));
  }

  // Set loading state
  loadingStates.placeOrder = true;
  notifyLoadingStateChange('placeOrder', true);
  console.log('Starting order placement process...');

  return new Promise((resolve, reject) => {
    let timeoutId = null;
    let isCleanedUp = false;

    // Cleanup function to prevent memory leaks and duplicate handlers
    const cleanup = () => {
      if (isCleanedUp) return;
      isCleanedUp = true;
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      websocketService.off('message', handleOrderResponse);
      
      // Reset loading state
      loadingStates.placeOrder = false;
      notifyLoadingStateChange('placeOrder', false);
    };

    // Set up response handler for order confirmation
    const handleOrderResponse = (response) => {
      if (response.type === 'order-response' || response.type === 'new-order-response') {
        console.log('Received order response:', response);
        
        cleanup();
        
        if (response.data && response.data.status === true) {
          // Order was accepted
          console.log('Order placement successful');
          resolve(response.data);
        } else {
          // Order placement failed
          const errorMessage = response.data?.message || 'Order placement failed';
          console.error('Order placement failed:', errorMessage);
          reject(new Error(errorMessage));
        }
      }
    };

    // Add order response handler
    websocketService.on('message', handleOrderResponse);

    // Set timeout for order response
    timeoutId = setTimeout(() => {
      console.log('Order placement timed out');
      cleanup();
      reject(new Error('Order placement timeout'));
    }, 30000); // 30 second timeout

    // Format the order message
    const message = {
      type: "place-order",
      data: {
        price: orderParams.price,
        quantity: orderParams.quantity,
        leverage: orderParams.leverage,
        orderType: orderParams.orderType,
        side: orderParams.side,
        symbol: orderParams.symbol,
        takeProfit: orderParams.takeProfit || null,
        stopLoss: orderParams.stopLoss || null,
        mode: orderParams.mode || "open"
      }
    };

    console.log('Sending order message:', message);

    // Send the order
    try {
      websocketService.send(message);
    } catch (error) {
      console.error('Error sending order message:', error);
      cleanup();
      reject(error);
    }
  });
};

/**
 * Cancel an existing order through the WebSocket connection
 * @param {string} orderId - ID of the order to cancel
 * @returns {Promise<Object>} - Resolves with the cancel response or rejects with an error
 */
export const cancelOrder = (orderId) => {
  // Validate orderId
  if (!orderId) {
    console.error('Cannot cancel order: Invalid order ID');
    return Promise.reject(new Error('Cannot cancel order: Invalid order ID'));
  }

  // Check if already loading
  if (loadingStates.cancelOrder) {
    console.log('Order cancellation already in progress, rejecting duplicate request');
    return Promise.reject(new Error('Order cancellation already in progress'));
  }

  // Set loading state
  loadingStates.cancelOrder = true;
  notifyLoadingStateChange('cancelOrder', true);
  console.log('Starting order cancellation process for order ID:', orderId);

  return new Promise((resolve, reject) => {
    let timeoutId = null;
    let isCleanedUp = false;

    // Cleanup function to prevent memory leaks and duplicate handlers
    const cleanup = () => {
      if (isCleanedUp) return;
      isCleanedUp = true;
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      websocketService.off('message', handleOrderResponse);
      
      // Reset loading state
      loadingStates.cancelOrder = false;
      notifyLoadingStateChange('cancelOrder', false);
    };

    // Set up response handler for cancel confirmation
    const handleOrderResponse = (response) => {
      if (response.type === 'remove-order-response') {
        console.log('Received cancel order response:', response);
        
        cleanup();
        
        if (response.data && response.data.status === true) {
          // Order cancellation was successful
          console.log('Order cancellation successful for order ID:', orderId);
          resolve(response.data);
        } else {
          // Order cancellation failed
          const errorMessage = response.data?.message || 'Order cancellation failed';
          console.error('Order cancellation failed:', errorMessage);
          reject(new Error(errorMessage));
        }
      }
    };

    // Add order response handler
    websocketService.on('message', handleOrderResponse);

    // Set timeout for order response
    timeoutId = setTimeout(() => {
      console.log('Order cancellation timed out for order ID:', orderId);
      cleanup();
      reject(new Error('Order cancellation timeout'));
    }, 30000); // 30 second timeout

    // Format and send the cancel message
    const message = {
      type: "cancel-order",
      data: {
        orderId: orderId
      }
    };

    console.log('Sending cancel order message:', message);

    // Send the cancel request
    try {
      websocketService.send(message);
    } catch (error) {
      console.error('Error sending cancel order message:', error);
      cleanup();
      reject(error);
    }
  });
};

/**
 * Check if an order operation is currently in progress
 * @param {string} operation - The operation to check ('placeOrder' or 'cancelOrder')
 * @returns {boolean} - True if the operation is in progress
 */
export const isOrderOperationInProgress = (operation) => {
  const isLoading = loadingStates[operation] || false;
  console.log(`Checking loading state for ${operation}: ${isLoading}`);
  return isLoading;
}; 